// ts-rest contract for the ingest-plan review page (SEP-252).
//
// A Family-A admin upload is now two steps. The multipart POST to
// `/api/admin/<door>/uploads` PREPARES a plan and writes no domain row; it sits
// outside ts-rest, like every other multipart door, and answers with the plan
// id. Everything after that — reading the plan, moving a selection, applying it
// and discarding it — is JSON, and it lives here.
//
// The wire carries what the reviewer decides FROM, and never the instruction
// the apply writes: a change crosses as its before/after pairs, and its payload
// stays on the server until the settle purges it.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodyWithCode as errorBody } from '../schemas/common';
import { backfillReportSchema } from '../schemas/client-book-backfill';
import { ingestDoorSchema } from './ingest-runs';

const c = initContract();

/** How a door offers its plan: per change, or the whole file at once. */
export const ingestPlanKindSchema = z.enum(['change_set', 'atomic_file']);
export type IngestPlanKind = z.infer<typeof ingestPlanKindSchema>;

/**
 * The disposition of one proposed change. It is the unit of selection: one
 * source row can want to fill four blank fields, replace two that hold a value
 * and change one that a person froze, and those are three separate decisions
 * with three different risks.
 */
export const planChangeGroupSchema = z.enum([
    /** A row the file names that does not exist yet. */
    'create',
    /** A field that holds nothing now, and the file fills it. */
    'fill_blank',
    /** A field that holds a value now, and the file replaces it. */
    'overwrite',
    /** A field a human froze, and the file would change it. */
    'frozen',
    /** An unowned row gains the owning IM the file's references resolve. */
    'adopt_owner',
    /** A relationship the file asserts and the world does not hold. */
    'create_link',
    /** A relationship the world holds and the file no longer asserts. */
    'end_link',
    /** An `atomic_file` door's whole-file replacement. */
    'replace',
    /** Reported, never applied: the row carries no usable change. */
    'skip',
]);
export type PlanChangeGroup = z.infer<typeof planChangeGroupSchema>;

export const ingestPlanStatusSchema = z.enum(['draft', 'applied', 'discarded', 'expired']);
export type IngestPlanStatus = z.infer<typeof ingestPlanStatusSchema>;

/** One line of the door's headline, above the change groups. */
export const planSummaryLineSchema = z.object({
    label: z.string(),
    value: z.string(),
    /** True when the line names something the reviewer must weigh, not a count. */
    warning: z.boolean().optional(),
});
export type PlanSummaryLine = z.infer<typeof planSummaryLineSchema>;

export const planFieldChangeSchema = z.object({
    field: z.string(),
    /** The stored value, rendered for the reviewer. Null means empty. */
    before: z.string().nullable(),
    /** What the file wants there. Null means the change clears the field. */
    after: z.string().nullable(),
});
export type PlanFieldChange = z.infer<typeof planFieldChangeSchema>;

/**
 * One value that a `create` change would write, as the reviewer reads it. A door
 * makes these lines from the payload of a DRAFT plan. The settle purges the
 * payload, so a settled plan shows no lines. Thus special-category content is
 * on the page only while a person decides.
 */
export const planPreviewLineSchema = z.object({
    /** The part of the record, for example a section of the fact find. */
    section: z.string(),
    label: z.string(),
    value: z.string(),
    /** The value that the change replaces. Absent on a line of a new row.
     *  Null when the field held nothing. */
    before: z.string().nullable().optional(),
});
export type PlanPreviewLine = z.infer<typeof planPreviewLineSchema>;

export const planChangeOutcomeSchema = z.enum([
    'applied',
    'rejected',
    'stale',
    'failed',
    'reported',
]);
export type PlanChangeOutcome = z.infer<typeof planChangeOutcomeSchema>;

export const planChangeSchema = z.object({
    id: z.string(),
    group: planChangeGroupSchema,
    targetType: z.string(),
    targetId: z.string().nullable(),
    /** Who or what the change is about, in the words of the business. */
    label: z.string(),
    /** The source row's own reference (CRM id, SUR number, portfolio ref). */
    sourceRef: z.string().nullable(),
    ownerImUserId: z.string().nullable(),
    /** The resolved display name of that IM, when the directory holds one. */
    ownerName: z.string().nullable(),
    fields: z.array(planFieldChangeSchema),
    /** The values the change would write. Empty when the door gives no preview,
     *  and empty after the settle. */
    preview: z.array(planPreviewLineSchema),
    selected: z.boolean(),
    /** False for a reported row that nothing can apply. */
    selectable: z.boolean(),
    reason: z.string().nullable(),
    detail: z.string().nullable(),
    outcome: planChangeOutcomeSchema.nullable(),
    outcomeDetail: z.string().nullable(),
});
export type PlanChange = z.infer<typeof planChangeSchema>;

export const planGroupTallySchema = z.object({
    group: planChangeGroupSchema,
    total: z.number().int().nonnegative(),
    selected: z.number().int().nonnegative(),
});
export type PlanGroupTally = z.infer<typeof planGroupTallySchema>;

/** One owner the plan's changes fall under. `ownerImUserId: null` is the
 *  unowned backlog, which is a real state and not a fault. */
export const planOwnerFacetSchema = z.object({
    ownerImUserId: z.string().nullable(),
    ownerName: z.string().nullable(),
    total: z.number().int().nonnegative(),
});
export type PlanOwnerFacet = z.infer<typeof planOwnerFacetSchema>;

export const ingestPlanSchema = z.object({
    id: z.string(),
    door: ingestDoorSchema,
    /** The door's name on the Admin shell. */
    doorLabel: z.string(),
    kind: ingestPlanKindSchema,
    status: ingestPlanStatusSchema,
    filename: z.string(),
    parsedRows: z.number().int().nonnegative(),
    preparedAt: z.string(),
    preparedByName: z.string().nullable(),
    expiresAt: z.string(),
    settledAt: z.string().nullable(),
    settledByName: z.string().nullable(),
    /** The IngestRun the apply wrote, once the plan is applied. */
    ingestRunId: z.string().nullable(),
    summary: z.array(planSummaryLineSchema),
});
export type IngestPlan = z.infer<typeof ingestPlanSchema>;

export const ingestPlanViewSchema = z.object({
    plan: ingestPlanSchema,
    groups: z.array(planGroupTallySchema),
    owners: z.array(planOwnerFacetSchema),
    /** The reason histogram of the reported rows, largest first. */
    reasons: z.array(z.object({ reason: z.string(), count: z.number().int().nonnegative() })),
});
export type IngestPlanView = z.infer<typeof ingestPlanViewSchema>;

export const ingestPlanChangesResponseSchema = z.object({
    changes: z.array(planChangeSchema),
    /** How many changes the filter matches, for the pager. */
    total: z.number().int().nonnegative(),
});
export type IngestPlanChangesResponse = z.infer<typeof ingestPlanChangesResponseSchema>;

/** The filter the review page drives. A selection takes the SAME filter, so
 *  "select every change I can currently see" is one call. */
export const planChangeFilterSchema = z.object({
    groups: z.array(planChangeGroupSchema).optional(),
    /** The string `unowned` selects the changes that name no IM. */
    owner: z.string().optional(),
    /** Matches the label, the source reference and the changed field names. */
    search: z.string().optional(),
    selected: z.boolean().optional(),
});
export type PlanChangeFilter = z.infer<typeof planChangeFilterSchema>;

export const planSelectionRequestSchema = z.object({
    selected: z.boolean(),
    /** Name the changes, or give a filter. Exactly one of the two. */
    changeIds: z.array(z.string()).optional(),
    filter: planChangeFilterSchema.optional(),
});

export const planSelectionResponseSchema = z.object({
    /** How many changes moved. */
    moved: z.number().int().nonnegative(),
    groups: z.array(planGroupTallySchema),
});

export const applyPlanResponseSchema = z.object({
    plan: ingestPlanSchema,
    report: backfillReportSchema,
});
export type ApplyPlanResponse = z.infer<typeof applyPlanResponseSchema>;

export const ingestPlansContract = c.router(
    {
        list: {
            method: 'GET',
            path: '/admin/ingest-plans',
            query: z.object({
                door: ingestDoorSchema.optional(),
                /** Omit for the open drafts; `any` for the recent plans whatever
                 *  became of them, which is what a record of decisions needs. */
                status: ingestPlanStatusSchema.or(z.literal('any')).optional(),
            }),
            responses: { 200: z.object({ plans: z.array(ingestPlanSchema) }) },
            summary: 'The open draft plans, newest first; `status` widens it',
        },
        get: {
            method: 'GET',
            path: '/admin/ingest-plans/:id',
            pathParams: z.object({ id: z.string() }),
            responses: { 200: ingestPlanViewSchema, 404: errorBody },
            summary: 'One plan: its headline, its group tallies and its owner facets',
        },
        changes: {
            method: 'GET',
            path: '/admin/ingest-plans/:id/changes',
            pathParams: z.object({ id: z.string() }),
            query: z.object({
                /** Comma-separated group keys. Absent means every group. */
                groups: z.string().optional(),
                owner: z.string().optional(),
                search: z.string().optional(),
                selected: z.enum(['true', 'false']).optional(),
                offset: z.coerce.number().int().nonnegative().optional(),
                limit: z.coerce.number().int().positive().max(500).optional(),
            }),
            responses: { 200: ingestPlanChangesResponseSchema, 404: errorBody },
            summary: 'One page of the plan changes a filter matches',
        },
        select: {
            method: 'POST',
            path: '/admin/ingest-plans/:id/selection',
            pathParams: z.object({ id: z.string() }),
            body: planSelectionRequestSchema,
            responses: {
                200: planSelectionResponseSchema,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Select or deselect named changes, or every change a filter matches',
        },
        apply: {
            method: 'POST',
            path: '/admin/ingest-plans/:id/apply',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}),
            responses: { 200: applyPlanResponseSchema, 404: errorBody, 409: errorBody },
            summary: 'Write the selected changes, and nothing else',
        },
        discard: {
            method: 'POST',
            path: '/admin/ingest-plans/:id/discard',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}),
            responses: { 200: z.object({ ok: z.literal(true) }), 404: errorBody, 409: errorBody },
            summary: 'Close a plan without writing anything, and drop the retained file',
        },
    },
    { pathPrefix: '/api' },
);
